import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Footer from '../components/Footer';
import {
  Sparkles,
  Megaphone,
  Home,
  FileText,
  DollarSign,
  Calendar,
  TrendingUp,
  BarChart3,
  Target,
  CheckCircle,
  ChevronRight,
} from 'lucide-react';

// ---------- Page-scoped CSS (things Tailwind can't express cleanly) ----------
const pageStyles = `
  .inn-serif { font-family: 'Playfair Display', Georgia, serif; }
  .inn-fp-eyebrow { font-family: 'Inter', system-ui, sans-serif; letter-spacing: 0.18em; font-size: 11px; text-transform: uppercase; font-weight: 600; color: #2FA163; }
  .inn-fp-hero-title {
    font-family: 'Playfair Display', Georgia, serif;
    font-weight: 600;
    font-size: clamp(30px, 6vw, 48px);
    line-height: 1.05;
    letter-spacing: -0.02em;
    color: #092825;
    margin: 0 0 14px;
    max-width: 18ch;
  }
  .inn-fp-pain {
    font-family: 'Playfair Display', Georgia, serif;
    font-style: italic;
    font-size: 13.5px;
    color: #6e7f73;
    line-height: 1.4;
    margin: 0 0 6px;
  }
  .inn-fp-pain::before { content: '\\201C'; }
  .inn-fp-pain::after  { content: '\\201D'; }

  .inn-fp-card {
    background: white;
    border: 1px solid #E7E8E1;
    border-radius: 14px;
    padding: 14px 16px;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    cursor: pointer;
    transition: border-color 0.15s ease, transform 0.15s ease;
    text-align: left;
    width: 100%;
  }
  .inn-fp-card:hover { border-color: #2FA163; transform: translateY(-1px); }
  .inn-fp-card-new { border-color: #2FA163; }

  .inn-fp-icon {
    width: 38px; height: 38px;
    border-radius: 10px;
    background: #EAF5EE;
    color: #2FA163;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }

  .inn-fp-new-badge {
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.06em;
    color: white;
    background: #2FA163;
    padding: 2px 6px;
    border-radius: 4px;
  }

  .inn-fp-cta-band {
    background: #092825;
    color: white;
    border-radius: 14px;
    padding: 22px 24px;
    text-align: center;
  }
  .inn-fp-cta-band h3 {
    font-family: 'Playfair Display', Georgia, serif;
    font-size: 22px;
    font-weight: 600;
    margin: 0 0 8px;
    color: white;
  }
  .inn-fp-cta-band p {
    font-size: 13.5px;
    color: rgba(255,255,255,0.75);
    margin: 0 0 14px;
    line-height: 1.45;
  }
`;

// ---------- Feature data (display order) ----------
const features = [
  {
    slug: 'ai-coach',
    title: 'Fairy AI Coach',
    icon: Sparkles,
    pain: "I don't know what I should focus on this week.",
    description: "Personal coaching that reads your P&L, deals, and activity — and tells you the one or two moves that'll move the needle this week.",
  },
  {
    slug: 'social-post',
    title: 'Social Post Generator',
    icon: Megaphone,
    isNew: true,
    pain: "I know I should post on social, but never know what to say.",
    description: "Pick a post type, tap Generate. Get a Facebook or Instagram post in your voice — using your market, your tone, and your recent deals.",
  },
  {
    slug: 'mortgage-calculator',
    title: 'Mortgage Calculator',
    icon: Home,
    pain: "My buyers don't actually know what they can afford.",
    description: "Show real monthly payments with taxes, PMI, and insurance baked in. Pre-educates buyers before the first showing.",
  },
  {
    slug: 'net-sheet',
    title: 'Seller Net Sheet',
    icon: FileText,
    pain: "Sellers fixate on sale price and get blindsided at closing.",
    description: "Walk sellers through their real proceeds at the listing appointment — branded, on your phone, before pricing gets emotional.",
  },
  {
    slug: 'commission-calculator',
    title: 'Commission Split Calculator',
    icon: DollarSign,
    pain: "I don't know what I'll actually take home until the check shows up.",
    description: "Calculate real take-home after splits, caps, broker fees, and referrals — with a branded PDF for your records.",
  },
  {
    slug: 'closing-date',
    title: 'Closing Date Calculator',
    icon: Calendar,
    pain: "Deadlines slip when they live in email threads and texts.",
    description: "Build a timeline from contract date — inspection, financing, appraisal, title, closing — and share one document everyone follows.",
  },
  {
    slug: 'deal-analyzer',
    title: 'Investor Deal Analyzer',
    icon: TrendingUp,
    pain: "Investor clients expect me to speak their language — I don't always know it.",
    description: "Cap rate, cash-on-cash, NOI, and break-even on any property in 30 seconds. No spreadsheet required.",
  },
  {
    slug: 'pnl-tracker',
    title: 'Agent P&L Tracker',
    icon: BarChart3,
    pain: "I track GCI but have no idea what I actually clear.",
    description: "Track real income and expenses by deal and lead source as they happen — see what's profitable and what's bleeding money.",
  },
  {
    slug: 'cap-report',
    title: 'Commission Cap Tracker',
    icon: Target,
    pain: "I think I've capped, but the math is fuzzy.",
    description: "See exactly how much you have left to your brokerage cap and when you'll hit it. Turns the cap from a yearly surprise into a monthly target.",
  },
  {
    slug: 'action-tracker',
    title: 'Action Tracker',
    icon: CheckCircle,
    pain: "By the time closings slow down, I'm already in trouble.",
    description: "Log calls, appointments, and follow-ups as they happen — the leading indicators of next quarter's pipeline, week over week.",
  },
];

// ---------- Single feature card ----------
const FeatureCard = ({ feature, onClick }) => {
  const Icon = feature.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inn-fp-card ${feature.isNew ? 'inn-fp-card-new' : ''}`}
      data-testid={`feature-card-${feature.slug}`}
      aria-label={`${feature.title} — learn more`}
    >
      <div className="inn-fp-icon">
        <Icon className="w-[18px] h-[18px]" strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1.5">
          <p className="text-[14.5px] font-semibold text-[#092825] m-0 leading-snug">{feature.title}</p>
          {feature.isNew && <span className="inn-fp-new-badge">NEW</span>}
        </div>
        <p className="inn-fp-pain">{feature.pain}</p>
        <p className="text-[12.5px] text-[#3d4b41] leading-relaxed m-0">{feature.description}</p>
      </div>
      <ChevronRight className="text-[#98AC9E] flex-shrink-0 self-center" size={14} strokeWidth={2} />
    </button>
  );
};

// ---------- Main page ----------
const FeaturesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const goToFeature = (slug) => navigate(`/features/${slug}`);
  const goToTrial = () => navigate(user ? '/dashboard' : '/auth/register');

  return (
    <>
      <style>{pageStyles}</style>
      <div className="min-h-screen bg-[#FAFAF7] text-[#092825]" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>

        {/* NAV */}
        <nav className="bg-white border-b border-[#E7E8E1] sticky top-0 z-40">
          <div className="max-w-3xl mx-auto px-5 md:px-6 flex items-center justify-between h-14">
            <button onClick={() => navigate('/')} className="flex items-center gap-2" aria-label="Go to home">
              <div className="w-7 h-7 rounded-full bg-[#2FA163] flex items-center justify-center text-white font-semibold text-sm">I</div>
              <span className="inn-serif font-semibold text-[12px] tracking-[0.18em] text-[#092825]">I NEED NUMBERS</span>
            </button>
            <button
              onClick={goToTrial}
              className="bg-[#2FA163] text-white text-[12px] font-medium px-3.5 py-1.5 rounded-full hover:bg-[#26895a] transition-colors"
            >
              Start trial
            </button>
          </div>
        </nav>

        {/* HERO */}
        <header className="max-w-3xl mx-auto px-5 md:px-6 pt-10 md:pt-14 pb-4 md:pb-6">
          <p className="inn-fp-eyebrow mb-3">FEATURES</p>
          <h1 className="inn-fp-hero-title">Every number an agent needs, in one place.</h1>
          <p className="text-[14.5px] md:text-[15px] text-[#4a5b50] leading-relaxed max-w-[60ch] m-0">
            Ten tools built for the real things agents actually struggle with — running their numbers, advising clients, and growing their book.
          </p>
        </header>

        {/* GRID */}
        <main className="max-w-3xl mx-auto px-4 md:px-6 pb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 md:gap-3.5">
            {features.map((f) => (
              <FeatureCard key={f.slug} feature={f} onClick={() => goToFeature(f.slug)} />
            ))}
          </div>
        </main>

        {/* CTA BAND */}
        <section className="max-w-3xl mx-auto px-4 md:px-6 pb-12">
          <div className="inn-fp-cta-band">
            <h3>See your numbers from day one.</h3>
            <p>Seven days free. No credit card to start.</p>
            <button
              onClick={goToTrial}
              className="bg-[#2FA163] text-white text-[13.5px] font-medium px-5 py-2.5 rounded-full hover:bg-[#26895a] transition-colors"
            >
              Start your trial
            </button>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default FeaturesPage;
